import React, { useState, useMemo } from 'react';
import { Download, FileSpreadsheet, Loader2, X, CheckCircle2, AlertCircle, RefreshCw, ChevronDown, ChevronUp } from 'lucide-react';
import { GenericColumn } from '../KanbanBoard/utils/tableColumns';

interface ColumnGroup {
  name: string;
  columns: string[];
}

interface ExportPreviewModalProps {
  onClose: () => void;
  onDownload: () => Promise<void>;
  dataCount: number;
  columns: GenericColumn<any>[];
  columnGroups?: ColumnGroup[];
  visibleColumns: Record<string, boolean>;
  setVisibleColumns: (columns: Record<string, boolean>) => void;
  tableVisibleColumns?: Record<string, boolean>;
  filename: string;
}

type DownloadStage = 'preparing' | 'generating' | 'downloading' | 'completed';

const NON_EXPORT_KEYS = new Set(['actions', 'select', 'checkbox']);

const ExportPreviewModal: React.FC<ExportPreviewModalProps> = ({
  onClose,
  onDownload,
  dataCount,
  columns,
  columnGroups,
  visibleColumns,
  setVisibleColumns,
  tableVisibleColumns,
  filename,
}) => {
  const [downloadStage, setDownloadStage] = useState<DownloadStage | null>(null);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [collapsedGroups, setCollapsedGroups] = useState<Record<string, boolean>>({});

  const exportableColumns = useMemo(
    () => columns.filter(col => !NON_EXPORT_KEYS.has(String(col.key ?? '')) && col.toCSV),
    [columns]
  );

  const exportColumns = exportableColumns.filter(col => visibleColumns[String(col.key ?? '')] === true);

  const isAllSelected = useMemo(
    () => exportableColumns.every(col => visibleColumns[String(col.key ?? '')] === true),
    [exportableColumns, visibleColumns]
  );

  const toggleColumn = (columnKey: string) => {
    setVisibleColumns({ ...visibleColumns, [columnKey]: !visibleColumns[columnKey] });
  };

  const toggleAll = () => {
    const newValue = !isAllSelected;
    const updated = exportableColumns.reduce((acc, col) => {
      acc[String(col.key ?? '')] = newValue;
      return acc;
    }, {} as Record<string, boolean>);
    setVisibleColumns(updated);
  };

  const toggleGroup = (groupColumns: string[], value: boolean) => {
    const updated = { ...visibleColumns };
    groupColumns.forEach(key => { updated[key] = value; });
    setVisibleColumns(updated);
  };

  const toggleGroupCollapse = (groupName: string) => {
    setCollapsedGroups(prev => ({ ...prev, [groupName]: !prev[groupName] }));
  };

  // Build groups: use provided columnGroups if available, else fall back to single flat group
  const resolvedGroups: ColumnGroup[] = useMemo(() => {
    if (columnGroups && columnGroups.length > 0) {
      return columnGroups
        .map(g => ({
          ...g,
          columns: g.columns.filter(key => exportableColumns.some(c => String(c.key) === key))
        }))
        .filter(g => g.columns.length > 0);
    }
    return [{ name: 'คอลัมน์', columns: exportableColumns.map(c => String(c.key)) }];
  }, [columnGroups, exportableColumns]);

  const columnLabelMap = useMemo(() => {
    const map: Record<string, string> = {};
    columns.forEach(col => { map[String(col.key ?? '')] = col.label; });
    return map;
  }, [columns]);

  const getStageInfo = (stage: DownloadStage) => {
    switch (stage) {
      case 'preparing':   return { text: 'กำลังเตรียมข้อมูล...', icon: Loader2, progress: 25 };
      case 'generating':  return { text: 'กำลังสร้างไฟล์...', icon: Loader2, progress: 50 };
      case 'downloading': return { text: 'กำลังดาวน์โหลด...', icon: Download, progress: 75 };
      case 'completed':   return { text: 'สำเร็จ', icon: CheckCircle2, progress: 100 };
      default:            return { text: '', icon: Loader2, progress: 0 };
    }
  };

  const handleDownload = async () => {
    setError(null);
    setDownloadStage('preparing');
    setProgress(25);
    await new Promise(resolve => setTimeout(resolve, 500));
    setDownloadStage('generating');
    setProgress(50);
    try {
      await onDownload();
      setDownloadStage('downloading');
      setProgress(75);
      await new Promise(resolve => setTimeout(resolve, 500));
      setDownloadStage('completed');
      setProgress(100);
      setTimeout(() => {
        onClose();
        setTimeout(() => {
          setDownloadStage(null);
          setProgress(0);
          setError(null);
        }, 300);
      }, 1500);
    } catch (err) {
      console.error('Download failed:', err);
      setError(err instanceof Error ? err.message : 'ไม่สามารถดาวน์โหลดไฟล์ได้');
      setDownloadStage(null);
      setProgress(0);
    }
  };

  const handleRetry = () => { setError(null); handleDownload(); };
  const handleDismissError = () => setError(null);

  const isDownloading = downloadStage !== null && downloadStage !== 'completed';
  const isCompleted = downloadStage === 'completed';
  const currentStageInfo = downloadStage ? getStageInfo(downloadStage) : null;
  const StageIcon = currentStageInfo?.icon || Loader2;

  return (
    <div className="fixed inset-0 z-40 flex items-center justify-center bg-black bg-opacity-50">
      <div className="bg-white rounded-lg shadow-lg w-full max-w-lg mx-4 flex flex-col max-h-[90vh]">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200">
          <div className="flex items-center gap-2">
            <FileSpreadsheet className="text-green-600" size={20} />
            <h2 className="text-lg font-semibold text-gray-900">โหลดทะเบียนแม่พิมพ์</h2>
          </div>
          <button onClick={onClose} disabled={isDownloading} className="text-gray-400 hover:text-gray-600 transition-colors disabled:opacity-50">
            <X size={20} />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4">
          {/* Error Section */}
          {error && (
            <div className="mb-4 p-4 bg-gradient-to-br from-red-50 to-orange-50 rounded-lg border border-red-200">
              <div className="flex items-start gap-3">
                <AlertCircle className="text-red-600 flex-shrink-0 mt-0.5" size={20} />
                <div className="flex-1">
                  <h3 className="text-sm font-semibold text-red-900 mb-1">เกิดข้อผิดพลาด</h3>
                  <p className="text-sm text-red-700 mb-3">{error}</p>
                  <div className="flex items-center gap-2">
                    <button onClick={handleRetry} className="px-3 py-1.5 text-xs font-medium text-white bg-red-600 hover:bg-red-700 rounded-lg flex items-center gap-1.5 transition-colors">
                      <RefreshCw size={14} /> ลองใหม่
                    </button>
                    <button onClick={handleDismissError} className="px-3 py-1.5 text-xs font-medium text-red-700 hover:bg-red-100 rounded-lg transition-colors">
                      ปิดข้อความ
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Progress Section */}
          {downloadStage && !error && (
            <div className="mb-4 p-4 bg-gradient-to-br from-green-50 to-emerald-50 rounded-lg border border-green-200">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <StageIcon className={`text-green-600 ${downloadStage !== 'completed' ? 'animate-pulse' : ''}`} size={20} />
                  <span className="text-sm font-medium text-gray-900">{currentStageInfo?.text}</span>
                </div>
                <span className="text-sm font-bold text-green-600">{progress}%</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2.5 overflow-hidden">
                <div
                  className="bg-gradient-to-r from-green-500 to-emerald-500 h-2.5 rounded-full transition-all duration-500 ease-out"
                  style={{ width: `${progress}%` }}
                >
                  <div className="h-full w-full animate-shimmer bg-gradient-to-r from-transparent via-white to-transparent opacity-30" />
                </div>
              </div>
              <div className="flex items-center justify-between mt-3">
                {(['preparing', 'generating', 'downloading', 'completed'] as DownloadStage[]).map((stage, stageIndex) => {
                  const stages: DownloadStage[] = ['preparing', 'generating', 'downloading', 'completed'];
                  const currentIndex = stages.indexOf(downloadStage);
                  const isActive = stageIndex <= currentIndex;
                  const stageLabels = ['เตรียม', 'สร้าง', 'ดาวน์โหลด', 'สำเร็จ'];
                  return (
                    <div key={stage} className="flex flex-col items-center gap-1">
                      <div className={`w-2 h-2 rounded-full transition-all duration-300 ${isActive ? stageIndex === currentIndex ? 'bg-green-500 animate-ping' : 'bg-green-500' : 'bg-gray-300'}`} />
                      <span className={`text-[10px] transition-colors duration-300 ${isActive ? 'text-green-700 font-medium' : 'text-gray-400'}`}>{stageLabels[stageIndex]}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {!downloadStage && !error && (
            <>
              {/* Summary bar */}
              <div className="flex items-center justify-between mb-3 p-3 bg-gray-50 rounded-lg">
                <span className="text-sm text-gray-600">จำนวน {dataCount.toLocaleString()} รายการ</span>
                <span className="text-sm font-medium text-gray-900">{exportColumns.length} คอลัมน์</span>
              </div>

              {/* Select all / sync from table */}
              <div className="mb-3 p-2 bg-gray-50 rounded-lg border border-gray-200 flex items-center justify-between gap-2">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={isAllSelected}
                    onChange={toggleAll}
                    className="form-checkbox h-4 w-4 text-green-600 rounded"
                  />
                  <span className="text-sm font-medium text-gray-800">เลือกทุกคอลัมน์</span>
                </label>
                {tableVisibleColumns && (
                  <button
                    onClick={() => {
                      const updated = exportableColumns.reduce((acc, col) => {
                        const key = String(col.key ?? '');
                        acc[key] = tableVisibleColumns[key] === true;
                        return acc;
                      }, {} as Record<string, boolean>);
                      setVisibleColumns({ ...visibleColumns, ...updated });
                    }}
                    className="text-xs text-blue-600 hover:text-blue-800 hover:underline whitespace-nowrap transition-colors"
                  >
                    ใช้คอลัมน์จากตาราง
                  </button>
                )}
              </div>

              {/* Grouped columns */}
              <div className="space-y-2">
                {resolvedGroups.map(group => {
                  const groupExportableKeys = group.columns.filter(k => exportableColumns.some(c => String(c.key) === k));
                  if (groupExportableKeys.length === 0) return null;
                  const allGroupSelected = groupExportableKeys.every(k => visibleColumns[k] === true);
                  const someGroupSelected = groupExportableKeys.some(k => visibleColumns[k] === true);
                  const isCollapsed = collapsedGroups[group.name] ?? false;

                  return (
                    <div key={group.name} className="border border-gray-200 rounded-lg overflow-hidden">
                      {/* Group header */}
                      <div className="flex items-center justify-between px-3 py-2 bg-gray-50 border-b border-gray-200">
                        <label className="flex items-center gap-2 cursor-pointer flex-1">
                          <input
                            type="checkbox"
                            checked={allGroupSelected}
                            ref={el => { if (el) el.indeterminate = !allGroupSelected && someGroupSelected; }}
                            onChange={() => toggleGroup(groupExportableKeys, !allGroupSelected)}
                            className="form-checkbox h-4 w-4 text-green-600 rounded"
                          />
                          <span className="text-sm font-medium text-gray-700">{group.name}</span>
                          <span className="text-xs text-gray-400 ml-1">
                            ({groupExportableKeys.filter(k => visibleColumns[k] === true).length}/{groupExportableKeys.length})
                          </span>
                        </label>
                        <button
                          onClick={() => toggleGroupCollapse(group.name)}
                          className="text-gray-400 hover:text-gray-600 transition-colors p-0.5"
                        >
                          {isCollapsed ? <ChevronDown size={14} /> : <ChevronUp size={14} />}
                        </button>
                      </div>

                      {/* Group columns */}
                      {!isCollapsed && (
                        <div className="p-2 grid grid-cols-2 gap-1 bg-white">
                          {groupExportableKeys.map(key => (
                            <label key={key} className="flex items-center gap-2 p-1.5 hover:bg-gray-50 rounded cursor-pointer transition-colors">
                              <input
                                type="checkbox"
                                checked={visibleColumns[key] === true}
                                onChange={() => toggleColumn(key)}
                                className="form-checkbox h-3.5 w-3.5 text-green-600 rounded flex-shrink-0"
                              />
                              <span className="text-xs text-gray-700 leading-tight">{columnLabelMap[key] ?? key}</span>
                            </label>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-2 p-4 border-t border-gray-200 bg-gray-50 rounded-b-lg">
          {error ? (
            <>
              <button onClick={handleDismissError} className="px-3 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded-lg transition-colors">
                ปิด
              </button>
              <button onClick={handleRetry} className="px-4 py-2 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-lg flex items-center gap-2 transition-colors">
                <RefreshCw className="w-4 h-4" /> ลองใหม่
              </button>
            </>
          ) : (
            <>
              <button onClick={onClose} disabled={isDownloading} className="px-3 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-50">
                ยกเลิก
              </button>
              <button
                onClick={handleDownload}
                disabled={isDownloading || exportColumns.length === 0}
                className={`px-4 py-2 text-sm font-medium text-white rounded-lg flex items-center gap-2 transition-all ${
                  isDownloading || isCompleted ? 'bg-green-500' : 'bg-green-600 hover:bg-green-700'
                } ${isDownloading || exportColumns.length === 0 ? 'opacity-50 cursor-not-allowed' : ''}`}
              >
                {isDownloading ? (
                  <><Loader2 className="w-4 h-4 animate-spin" /><span>{currentStageInfo?.text}</span></>
                ) : isCompleted ? (
                  <><CheckCircle2 className="w-4 h-4" /><span>สำเร็จ</span></>
                ) : (
                  <><Download className="w-4 h-4" /><span>ดาวน์โหลด ({exportColumns.length} คอลัมน์)</span></>
                )}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default ExportPreviewModal;
